import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";

import { Substitution } from "../../../api/types/substitutions";
import { SearchBar } from "../../../components/SearchBar";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch";
import { useModal } from "../../../hooks/useModal";
import { useSubstitutions } from "../api/useSubstitutions";

import { DeleteSubstitutionModal } from "./modals/DeleteSubstitutionModal";

type Props = { datasetId?: number };

export function SubstitutionsTable({ datasetId }: Props) {
  const { substitutions } = useSubstitutions(datasetId);
  const { formatMessage } = useIntl();
  const [selected, setSelected] = useState<Substitution | null>(null);
  const { open, handleOpen, handleClose } = useModal();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [searchParamValue, setSearchParamValue] = useDebouncedSearch(
    "search",
    300,
  );

  const rows = useMemo(() => substitutions ?? [], [substitutions]);

  const handleDelete = (item: Substitution) => {
    setSelected(item);
    handleOpen();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return rows.slice(startIndex, startIndex + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  const getSubstitutionType = (substitution: Substitution): string => {
    if (substitution.OriginalPlantId || substitution.ReplacementPlantId) {
      return "Plant";
    }
    if (substitution.OriginalInputId || substitution.ReplacementInputId) {
      return "Input";
    }
    return "Unknown";
  };

  return (
    <>
      <div className="mb-8">
        <SearchBar
          initialValue={searchParamValue ?? ""}
          onChange={value => setSearchParamValue(value)}
        />
      </div>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  {formatMessage({
                    id: "SUBSTITUTIONS.TYPE",
                    defaultMessage: "Type",
                  })}
                </TableCell>
                <TableCell>
                  {formatMessage({
                    id: "SUBSTITUTIONS.ORIGINAL_ID",
                    defaultMessage: "Original CAFS ID",
                  })}
                </TableCell>
                <TableCell>
                  {formatMessage({
                    id: "SUBSTITUTIONS.ORIGINAL_NAME",
                    defaultMessage: "Original CAFS name",
                  })}
                </TableCell>
                <TableCell>
                  {formatMessage({
                    id: "SUBSTITUTIONS.REPLACEMENT_ID",
                    defaultMessage: "Replacement CAFS ID",
                  })}
                </TableCell>
                <TableCell>
                  {formatMessage({
                    id: "SUBSTITUTIONS.REPLACEMENT_NAME",
                    defaultMessage: "Replacement CAFS name",
                  })}
                </TableCell>
                <TableCell align="right">
                  {formatMessage({
                    id: "APP.ACTIONS",
                    defaultMessage: "Actions",
                  })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map(row => (
                <TableRow key={row.Id!}>
                  <TableCell>{getSubstitutionType(row)}</TableCell>
                  <TableCell>
                    {row.OriginalInputId || row.OriginalPlantId || ""}
                  </TableCell>
                  <TableCell>
                    {row.OriginalInputName ||
                      row.OriginalPlantName ||
                      formatMessage({
                        id: "SUBSTITUTIONS.NOT_AVAILABLE",
                        defaultMessage: "Not available",
                      })}
                  </TableCell>
                  <TableCell>
                    {row.ReplacementInputId || row.ReplacementPlantId || ""}
                  </TableCell>
                  <TableCell>
                    {row.ReplacementInputName ||
                      row.ReplacementPlantName ||
                      formatMessage({
                        id: "SUBSTITUTIONS.NOT_AVAILABLE",
                        defaultMessage: "Not available",
                      })}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleDelete(row)}
                      size="small">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className="text-center">
                      {formatMessage({
                        id: "APP.NO_ITEMS",
                        defaultMessage: "No items",
                      })}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <DeleteSubstitutionModal
        open={open}
        onClose={handleClose}
        substitution={selected}
        datasetId={datasetId}
      />
    </>
  );
}
